﻿import React, { useCallback } from 'react';
import { FormField } from '../../../ui/FormField';
import type { ScheduleSummary, TimeZoneSummary } from '../../../../services/configuration/configuration.types';

type FieldName = 'profileName' | 'email' | 'sms' | 'firstName' | 'lastName' | 'scheduleId' | 'timeZoneId';
type RecipientValue = string | number | boolean | undefined;
type ChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLSelectElement>;
type FieldErrors = Partial<Record<FieldName, string>>;

interface Recipient {
    __uid: string;
    isBureauRecipient?: boolean;
    bureauContactType?: string;
    contactType?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    sms?: string;
    scheduleId?: number | null;
    timeZoneId?: number | null;
    isEditing?: boolean;
}

interface SharedRowProps {
    r: Recipient;
    origIndex: number;
    errors: Record<number, FieldErrors>;
    schedules: ScheduleSummary[];
    timeZones: TimeZoneSummary[];
    handleChangeFormField: (index: number, field: FieldName) => (e: ChangeEvent) => void;
    handleRecipientChange: (uid: string, field: string, value: RecipientValue) => void;
    handleRemoveRecipient: (uid: string, index: number) => Promise<void> | void;
}

interface RecipientRowProps extends SharedRowProps {
    variant?: 'entry' | 'table';
}

function getBureauState(r: Recipient) {
    const isBureau = r.isBureauRecipient === true;
    const isBureauOnly = isBureau && r.bureauContactType === 'bureau';
    const showEmail = !isBureauOnly && !(isBureau && r.bureauContactType === 'sms');
    const showSms   = !isBureauOnly && !(isBureau && r.bureauContactType === 'email');
    return { isBureau, isBureauOnly, showEmail, showSms };
}

function toOptionalNumber(value: string): number | undefined {
    if (value) {
        return Number(value);
    }
    return undefined;
}

function getRecipientName(r: Recipient): string {
    if (r.firstName || r.lastName) {
        return `${r.firstName || ''} ${r.lastName || ''}`.trim();
    }
    if (r.isEditing) {
        return 'New Recipient';
    }
    return 'Recipient';
}

function getTimeZoneClass(hasError: unknown): string {
    if (hasError) {
        return 'form__control form__control--error';
    }
    return 'form__control';
}

/** Applies bureau checkbox logic â€” extracted to keep component complexity â‰¤ 10. */
function applyBureauChange(
    uid: string,
    bureauContactType: string | undefined,
    handleRecipientChange: (uid: string, field: string, value: RecipientValue) => void,
    checked: boolean,
): void {
    handleRecipientChange(uid, 'isBureauRecipient', checked);
    if (checked && bureauContactType !== 'bureau') {
        handleRecipientChange(uid, 'bureauContactType', 'bureau');
    }
}

function numericSelectValue(value: number | null | undefined): number | string {
    if (value !== undefined && value !== null) {
        return value;
    }
    return '';
}

function renderScheduleOption(s: ScheduleSummary) {
    return <option key={s.id} value={s.id}>{s.name}</option>;
}

function renderTimeZoneOption(tz: TimeZoneSummary) {
    const label = tz.standardName ? `${tz.displayName} - ${tz.standardName}` : tz.displayName;
    return <option key={tz.id} value={tz.id}>{label}</option>;
}

const RecipientTableRow: React.FC<SharedRowProps> = ({
    r, origIndex, errors, schedules, timeZones,
    handleChangeFormField, handleRecipientChange, handleRemoveRecipient,
}) => {
    const { isBureau, isBureauOnly, showEmail, showSms } = getBureauState(r);

    const onBureauChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => applyBureauChange(r.__uid, r.bureauContactType, handleRecipientChange, e.target.checked),
        [r.__uid, r.bureauContactType, handleRecipientChange],
    );
    const onFirstNameChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'firstName')(e); handleRecipientChange(r.__uid, 'firstName', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onLastNameChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'lastName')(e); handleRecipientChange(r.__uid, 'lastName', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onScheduleChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'scheduleId')(e); handleRecipientChange(r.__uid, 'scheduleId', toOptionalNumber(e.target.value)); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onEmailChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'email')(e); handleRecipientChange(r.__uid, 'email', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onSmsChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'sms')(e); handleRecipientChange(r.__uid, 'sms', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onTimeZoneChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'timeZoneId')(e); handleRecipientChange(r.__uid, 'timeZoneId', toOptionalNumber(e.target.value)); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onRemove = useCallback(
        () => handleRemoveRecipient(r.__uid, origIndex),
        [r.__uid, origIndex, handleRemoveRecipient],
    );

    return (
        <tr key={r.__uid}>
            <td>
                <input
                    type="checkbox"
                    checked={isBureau}
                    onChange={onBureauChange}
                    title="CSH Recipient"
                />
            </td>
            <td>
                {!isBureauOnly && (
                    <FormField
                        placeholder="First name"
                        name="First name"
                        type="text"
                        value={r.firstName || ''}
                        showErrorMessage={false}
                        enableCopyPaste={false}
                        onChange={onFirstNameChange}
                        error={errors[origIndex]?.firstName}
                    />
                )}
            </td>
            <td>
                {!isBureauOnly && (
                    <FormField
                        placeholder="Last name"
                        name="Last name"
                        type="text"
                        value={r.lastName || ''}
                        showErrorMessage={false}
                        enableCopyPaste={false}
                        onChange={onLastNameChange}
                        error={errors[origIndex]?.lastName}
                    />
                )}
            </td>
            <td className='distribution_form selection'>
                <select
                    className="form__control"
                    value={numericSelectValue(r.scheduleId)}
                    onChange={onScheduleChange}
                >
                    {schedules.map(renderScheduleOption)}
                </select>
            </td>
            {/* Conditionally show email/sms based on bureauContactType when CSH recipient */}
            <td>
                {showEmail && (
                    <FormField
                        placeholder="Email address"
                        name="Email address"
                        type="email"
                        value={r.email || ''}
                        showErrorMessage={false}
                        onChange={onEmailChange}
                        error={errors[origIndex]?.email}
                    />
                )}
            </td>
            <td>
                {showSms && (
                    <FormField
                        placeholder="SMS number"
                        name="SMS number"
                        type="text"
                        value={r.sms || ''}
                        showErrorMessage={false}
                        enableCopyPaste={true}
                        onChange={onSmsChange}
                        error={errors[origIndex]?.sms}
                    />
                )}
            </td>
            <td className='distribution_form selection'>
                <select
                    className={getTimeZoneClass(errors[origIndex]?.timeZoneId)}
                    value={numericSelectValue(r.timeZoneId)}
                    onChange={onTimeZoneChange}
                >
                    {timeZones.map(renderTimeZoneOption)}
                </select>
            </td>
            <td className='distribution_form button_submit'>
                <button type="button" className="btn btn-link text-danger" onClick={onRemove}>Remove</button>
            </td>
        </tr>
    );
};


const RecipientEntryCard: React.FC<SharedRowProps> = ({
    r, origIndex, errors, schedules, timeZones,
    handleChangeFormField, handleRecipientChange, handleRemoveRecipient,
}) => {
    const { isBureauOnly } = getBureauState(r);
    const recipientName = getRecipientName(r);

    const bureauCheckboxId = `bureau-${r.__uid}`;
    const scheduleSelectId = `schedule-${r.__uid}`;
    const timeZoneSelectId = `timezone-${r.__uid}`;
    
    let contactMethod: 'email' | 'sms';
    if (r.bureauContactType === 'sms' || r.bureauContactType === 'email') {
        contactMethod = r.bureauContactType;
    } else if (typeof r.contactType === 'string' && r.contactType.trim().toLowerCase() === 'sms') {
        contactMethod = 'sms';
    } else {
        contactMethod = 'email';
    }

    // Handlers for radio buttons
    const onContactMethodChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleRecipientChange(r.__uid, 'bureauContactType', value);
            // Clear the other field when switching
            if (value === 'email') {
                handleRecipientChange(r.__uid, 'sms', '');
            } else if (value === 'sms') {
                handleRecipientChange(r.__uid, 'email', '');
            }
        },
        [r.__uid, handleRecipientChange],
    );

    const onBureauChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => applyBureauChange(r.__uid, r.bureauContactType, handleRecipientChange, e.target.checked),
        [r.__uid, r.bureauContactType, handleRecipientChange],
    );
    const onFirstNameChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'firstName')(e); handleRecipientChange(r.__uid, 'firstName', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onLastNameChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'lastName')(e); handleRecipientChange(r.__uid, 'lastName', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onEmailChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'email')(e); handleRecipientChange(r.__uid, 'email', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onSmsChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'sms')(e); handleRecipientChange(r.__uid, 'sms', e.target.value); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onScheduleChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'scheduleId')(e); handleRecipientChange(r.__uid, 'scheduleId', toOptionalNumber(e.target.value)); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onTimeZoneChange = useCallback(
        (e: ChangeEvent) => { handleChangeFormField(origIndex, 'timeZoneId')(e); handleRecipientChange(r.__uid, 'timeZoneId', toOptionalNumber(e.target.value)); },
        [origIndex, r.__uid, handleChangeFormField, handleRecipientChange],
    );
    const onRemove = useCallback(
        () => handleRemoveRecipient(r.__uid, origIndex),
        [r.__uid, origIndex, handleRemoveRecipient],
    );

    return (
        <div key={r.__uid} className="form-entry form-entry--column mb-2">
            <div className="form-entry__heading">
                <h3 className="heading__title--building">{recipientName}</h3>
                <button
                    type="button"
                    className="btn__close se-icon--close"
                    onClick={onRemove}
                ></button>
            </div>

            <div className="form-row__select">
                <input
                    id={bureauCheckboxId}
                    type="checkbox"
                    checked={r.isBureauRecipient === true}
                    onChange={onBureauChange}
                />
                <label htmlFor={bureauCheckboxId}>CSH Recipient</label>
            </div>
           
           {/* Bureau-contact radios removed: checkbox alone controls bureau behavior */}
           
            {!isBureauOnly && (
                <div className='form-row__group'>
                    <div className="form-row">
                        <label htmlFor={`firstName-${r.__uid}`}>First Name</label>
                        <FormField
                            placeholder="First name"
                            name={`firstName-${r.__uid}`}
                            type="text"
                            value={r.firstName || ''}
                            showErrorMessage={false}
                            enableCopyPaste={false}
                            onChange={onFirstNameChange}
                            error={errors[origIndex]?.firstName}
                        />
                    </div>
                    <div className="form-row">
                        <label htmlFor={`lastName-${r.__uid}`}>Last Name</label>
                        <FormField
                            placeholder="Last name"
                            name={`lastName-${r.__uid}`}
                            type="text"
                            value={r.lastName || ''}
                            showErrorMessage={false}
                            enableCopyPaste={false}
                            onChange={onLastNameChange}
                            error={errors[origIndex]?.lastName}
                        />
                    </div>
                </div>
            )}

               {!isBureauOnly && (
                <div className="form-row__group" style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <input
                                type="radio"
                                name={`contact-method-${r.__uid}`}
                                value="email"
                                checked={contactMethod === 'email'}
                                onChange={onContactMethodChange}
                                disabled={false} 
                            />
                            Email
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <input
                                type="radio"
                                name={`contact-method-${r.__uid}`}
                                value="sms"
                                checked={contactMethod === 'sms'}
                                onChange={onContactMethodChange}
                                disabled={false}
                            />
                            SMS
                        </label>
                    </div>
                </div>
            )}

            {!isBureauOnly && contactMethod === 'email' && (
                <div className="form-row">
                    <label htmlFor={`email-${r.__uid}`}>Email</label>
                    <FormField
                        placeholder="Email address"
                        name={`email-${r.__uid}`}
                        type="email"
                        value={r.email || ''}
                        showErrorMessage={false}
                        onChange={onEmailChange}
                        error={errors[origIndex]?.email}
                    />
                </div>
            )}
            {!isBureauOnly && contactMethod === 'sms' && (
                <div className="form-row">
                    <label htmlFor={`sms-${r.__uid}`}>SMS</label>
                    <FormField
                        placeholder="SMS number"
                        name={`sms-${r.__uid}`}
                        type="text"
                        value={r.sms || ''}
                        showErrorMessage={false}
                        enableCopyPaste={true}
                        onChange={onSmsChange}
                        error={errors[origIndex]?.sms}
                    />
                </div>
            )}

            <div className="form-row">
                <label htmlFor={scheduleSelectId}>Schedule</label>
                <select
                    id={scheduleSelectId}
                    className="form__control"
                    value={numericSelectValue(r.scheduleId)}
                    onChange={onScheduleChange}
                >
                    {schedules.map(renderScheduleOption)}
                </select>
            </div>

            <div className="form-row mb-2">
                <label htmlFor={timeZoneSelectId}>Time Zone</label>
                <select
                    id={timeZoneSelectId}
                    className={getTimeZoneClass(errors[origIndex]?.timeZoneId)}
                    value={numericSelectValue(r.timeZoneId)}
                    onChange={onTimeZoneChange}
                >
                    {timeZones.map(renderTimeZoneOption)}
                </select>
            </div>
        </div>
    );
};


export const RecipientRow: React.FC<RecipientRowProps> = ({ variant = 'entry', ...shared }) => {
    if (variant === 'table') {
        return <RecipientTableRow {...shared} />;
    }
    return <RecipientEntryCard {...shared} />;
};

export default RecipientRow;